import { useState } from "react";
import { useParams } from "react-router";
import { berlinZeitpunkt, type Anrede } from "@dj/domain/gemeinsam";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { Abweisung, Meldung, Status } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument } from "../plattform/lesemodelle";
import { zugangsstatus } from "./Teilnehmeruebersicht";

/**
 * Teilnehmer (oberflaeche.md §7, §8.5): „Einladungslink“ erzeugt die Einladung; der Link steht nur in der Antwort
 * (K7) und lässt sich deshalb nur jetzt kopieren oder teilen — danach wird neu ausgestellt.
 */
/** Der Link steht nur in der ersten Antwort; eine wiederholte Übertragung (befehle.md §5) bringt ihn nicht noch einmal. */
function linkAus(felder: Record<string, unknown>): string | null {
  return typeof felder["einladungslink"] === "string" ? felder["einladungslink"] : null;
}

export function Teilnehmer() {
  const { revierId = "", teilnehmerId = "" } = useParams();
  const z = useDokument(`reviere/${revierId}/teilnehmeruebersicht/${teilnehmerId}`);
  const [link, setLink] = useState<string | null>(null);
  const [grund, setGrund] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  const t = z.daten?.["Teilnehmer"] as { Name: string; Anrede: Anrede } | undefined;

  async function erzeugen() {
    setGrund(null);
    setLaeuft(true);
    const a = await befehl("EinladungAusstellen", { Revier: revierId, Teilnehmer: teilnehmerId });
    setLaeuft(false);
    if (a.ok) setLink(linkAus(a.felder));
    else setGrund(abweisungsgrund(a));
  }

  async function pflegen(name: "EinladungDeaktivieren" | "EinladungReaktivieren" | "EinladungNeuAusstellen" | "ZugangEntziehen") {
    setGrund(null);
    setLaeuft(true);
    const a = await befehl(name, { Revier: revierId, Teilnehmer: teilnehmerId });
    setLaeuft(false);
    if (!a.ok) setGrund(abweisungsgrund(a));
    else if (name === "EinladungNeuAusstellen") { setKopiert(false); setLink(linkAus(a.felder)); }
  }

  async function kopieren() {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setKopiert(true);
  }

  async function teilen() {
    if (!link) return;
    try {
      await navigator.share({ title: "Einladung", url: link });
    } catch {
      // abgebrochen: nichts zu tun
    }
  }

  const s = z.daten ? zugangsstatus(z.daten) : null;
  const [entziehen, setEntziehen] = useState(false);
  const hatEinladung = !!z.daten?.["Einladung"];
  return (
    <Ansicht name={t?.Name ?? "Teilnehmer"} symbol="teilnehmer" unterzeile={t ? t.Anrede : undefined}>
      {z.daten ? (
        <div className="liste-bereich platte">
          <dl className="angaben">
            <dt>Anrede</dt><dd>{t?.Anrede}</dd>
            {z.daten["Rolle im Revier"] ? <><dt>Rolle im Revier</dt><dd>{String(z.daten["Rolle im Revier"])}</dd></> : null}
            {z.daten["Telefonnummer"] ? <><dt>Telefonnummer</dt><dd>{String(z.daten["Telefonnummer"])}</dd></> : null}
            <dt>Zugang</dt><dd>{s?.text}</dd>
            {z.daten["zuletzt angemeldet"] instanceof Date ? <><dt>zuletzt angemeldet</dt><dd>{berlinZeitpunkt(z.daten["zuletzt angemeldet"] as Date)}</dd></> : null}
          </dl>
        </div>
      ) : null}
      <div className="knopfreihe">
        <Knopf art="planung" symbol="einladung" onPress={erzeugen} isDisabled={laeuft || !!link || hatEinladung}>Einladungslink</Knopf>
        {hatEinladung ? (
          <>
            <Knopf art="planung-zweit" symbol="sperre" onPress={() => pflegen("EinladungDeaktivieren")} isDisabled={laeuft}>Deaktivieren</Knopf>
            <Knopf art="planung-zweit" symbol="wiederherstellung" onPress={() => pflegen("EinladungReaktivieren")} isDisabled={laeuft}>Reaktivieren</Knopf>
            <Knopf art="planung-zweit" symbol="einladung" onPress={() => pflegen("EinladungNeuAusstellen")} isDisabled={laeuft}>Erneuern</Knopf>
          </>
        ) : null}
        {String(z.daten?.["Zugang"] ?? "").startsWith("Passkey") ? (
          <Knopf art="planung-zweit" symbol="schliessen" onPress={() => setEntziehen(true)} isDisabled={laeuft}>Zugang</Knopf>
        ) : null}
      </div>
      <Dialog name="Zugang" symbol="sperre" offen={entziehen} bestaetigung onSchliessen={() => setEntziehen(false)}>
        <p>{`Zugang von ${t?.Name ?? "Teilnehmer"} zu diesem Revier entziehen? Konto und andere Reviere bleiben unberührt.`}</p>
        <div className="knopfreihe">
          <Knopf onPress={() => { setEntziehen(false); void pflegen("ZugangEntziehen"); }}>Bestätigen</Knopf>
          <Knopf art="zweit" onPress={() => setEntziehen(false)}>Abbrechen</Knopf>
        </div>
      </Dialog>
      <Abweisung grund={grund} />
      {link ? (
        <section className="text-bereich platte" role="region" aria-label="Einladungslink">
          <h2 className="kopf">Einladungslink</h2>
          <div className="linkfeld">{link}</div>
          <p className="erkl">Nur jetzt kopierbar. Wer den Link später braucht, stellt die Einladung neu aus.</p>
          <div className="knopfreihe">
            <Knopf art="planung" symbol="link" onPress={kopieren}>Kopieren</Knopf>
            <Knopf art="planung-zweit" symbol="senden" onPress={teilen}>Teilen</Knopf>
          </div>
          {kopiert ? <Status symbol="haken">Kopiert</Status> : null}
        </section>
      ) : null}
      {z.fehler ? <Meldung>{z.fehler}</Meldung> : null}
    </Ansicht>
  );
}
